import { useState, useEffect, useCallback } from 'react';
import './Banner.css';
import Layout from '../../../components/Layout';
import AdminNavbar from '../../../components/Admin/AdminNavbar/AdminNavbar';
import CreateNewBannerModel from '../../../components/Admin/Banner/CreateNewBannerModel/CreateNewBannerModel';
import DeleteBanner from '../../../components/Admin/Banner/DeleteBanner/DeleteBanner';
import { Box, IconButton, Tooltip, Typography, Input, MenuItem, Select } from '@mui/material';
import { styled } from '@mui/material/styles';
import usePagination from '@mui/material/usePagination';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '../../../assets/Admin/Banner/delete.svg';
import { axiosInstance } from '../../../utils/tokenUtils';
import { formatDate } from '../../../utils/dateUtils';
import { toast } from 'react-hot-toast';
import BannerSkeleton from '../../../components/SkeletonEffect/BannerSkeleton/BannerSkeleton';
import MobileBannerPreview from '../../../components/Admin/Banner/MobileBannerPreview/MobileBannerPreview';
import '../../../components/Admin/Banner/MobileBannerPreview/MobileBannerPreview.css';
import Lottie from 'lottie-react';
import disputeAnimation from '../../../assets/Admin/gif/Dispute.json';

const DEFAULT_PAGE_SIZE = 20;

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const BannerFallback = ({ title, placement }) => {
  return (
    <div className="banner-list-mobile-preview">
      <MobileBannerPreview title={title} placement={placement} />
    </div>
  );
};

const Banner = () => {
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    bannerId: null,
    bannerTitle: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const fetchBanners = useCallback(
    async (pageNumber = page, size = pageSize, search = debouncedSearchTerm) => {
      try {
        setLoading(true);
        const params = {
          page: pageNumber,
          page_size: size,
        };
        if (search?.trim()) params.search_query = search.trim();
        const response = await axiosInstance.get('/admin/banners', { params });
        setBanners(response.data.results || []);
        setTotalCount(response.data.count || 0);
        setPage(pageNumber);
        setPageSize(size);
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBanners([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSearchTerm]
  );

  useEffect(() => {
    setPage(1);
    fetchBanners(1, pageSize, debouncedSearchTerm);
    // Reset to first page when search changes; pageSize is intentional here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const { items } = usePagination({
    count: Math.ceil((totalCount || 0) / (pageSize || DEFAULT_PAGE_SIZE)) || 1,
    page,
    onChange: (event, value) => {
      fetchBanners(value, pageSize, debouncedSearchTerm);
    },
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    const id = deleteModal.bannerId;
    if (id) {
      try {
        await axiosInstance.delete(`/admin/banners/${id}`);
        const newErrors = { ...imageLoadErrors };
        delete newErrors[id];
        setImageLoadErrors(newErrors);

        toast.success('Banner deleted successfully');

        const remainingOnPage = banners.length - 1;
        const nextPage = remainingOnPage === 0 && page > 1 ? page - 1 : page;
        await fetchBanners(nextPage, pageSize, debouncedSearchTerm);
      } catch (error) {
        console.error('Error deleting banner:', error);
      } finally {
        setDeleteModal({ isOpen: false, bannerId: null, bannerTitle: '' });
      }
    }
  };

  const handleDeleteClick = id => {
    const banner = banners.find(banner => banner.id === id);
    setDeleteModal({
      isOpen: true,
      bannerId: id,
      bannerTitle: banner?.title || 'this banner',
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ isOpen: false, bannerId: null, bannerTitle: '' });
  };

  const handleImageError = id => {
    setImageLoadErrors(prev => ({
      ...prev,
      [id]: true,
    }));
  };

  const handleBannerCreated = () => {
    fetchBanners(1, pageSize, debouncedSearchTerm);
    handleCloseModal();
  };

  const handleRowsPerPageChange = event => {
    const newPageSize = Number(event.target.value);
    fetchBanners(1, newPageSize, debouncedSearchTerm);
  };

  return (
    <>
      <Layout title="Klass Online | Banners">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <div className="banner-container">
              <div className="banner-header">
                <Box className="banner-title">Banners</Box>
                <Box className="banner-header-actions">
                  <Input
                    sx={{
                      textDecoration: 'none',
                      border: '1px solid var(--Stroke, #D5DEE3)',
                      borderRadius: '56px',
                      '&:before, &:after': { borderBottom: 'none !important' },
                      '&:hover:not(.Mui-disabled):before': { borderBottom: 'none !important' },
                      padding: '8px 10px',
                      '& .MuiInputBase-input': { padding: '3px 5px' },
                      minWidth: '240px',
                      height: '40px',
                    }}
                    type="search"
                    placeholder="Search by title, placement, or redirect..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    startAdornment={<SearchIcon className="search-icon-css" />}
                  />
                  <button className="create-banner-btn" onClick={handleOpenModal}>
                    <span>+</span> Create Banner
                  </button>
                </Box>
              </div>
              <div className="banner-content">
                {loading ? (
                  <BannerSkeleton />
                ) : banners.length > 0 ? (
                  <>
                    <div className="banners-list">
                      {banners.map(banner => (
                        <div key={banner.id} className="banner-item">
                          <div className="banner-preview">
                            {!imageLoadErrors[banner.id] && banner.image_url ? (
                              <div className="banner-list-mobile-preview">
                                <MobileBannerPreview
                                  imageUrl={banner.image_url}
                                  title={banner.title}
                                  placement={banner.placement}
                                  onImageError={() => handleImageError(banner.id)}
                                />
                              </div>
                            ) : (
                              <BannerFallback title={banner.title} placement={banner.placement} />
                            )}
                          </div>
                          <div className="banner-details">
                            <div className="banner-info">
                              {banner.title?.trim() && (
                                <div className="banner-field">
                                  <label className="title-label">Title</label>
                                  <div className="title-value">{banner.title}</div>
                                </div>
                              )}
                              {banner.placement?.trim() && (
                                <div className="banner-field">
                                  <label className="title-label">Description</label>
                                  <div className="title-value">{banner.placement}</div>
                                </div>
                              )}
                              <div className="banner-field">
                                <label className="title-label">Get Clicking Redirect to</label>
                                <div className="title-value">
                                  {banner.navigate_to || 'No redirect'}
                                </div>
                              </div>
                              {banner.expiry_date && (
                                <div className="banner-field">
                                  <label className="title-label">Expiry Date</label>
                                  <div className="title-value">
                                    {formatDate(banner.expiry_date, 'DD/MM/YYYY')}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="banner-actions">
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteClick(banner.id)}
                                  size="small"
                                  className="delete-btn"
                                >
                                  <img src={DeleteIcon} alt="Delete" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Box
                      className="banner-pagination"
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '16px',
                        paddingTop: '8px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            color: '#666',
                          }}
                        >
                          Showing
                        </Typography>
                        <Select
                          value={pageSize}
                          onChange={handleRowsPerPageChange}
                          sx={{
                            height: '30px',
                            minWidth: '20px',
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            '& .MuiSelect-select': {
                              padding: '6px 4px 4px 8px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={30}>30</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            color: '#666',
                          }}
                        >
                          of {totalCount} Banners
                        </Typography>
                      </Box>
                      <nav>
                        <List>
                          {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                            let children = null;

                            if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                              children = '…';
                            } else if (type === 'page') {
                              children = (
                                <button
                                  type="button"
                                  style={{
                                    fontWeight: selected ? 'bold' : undefined,
                                    margin: '0 5px',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                    fontFamily: 'Poppins',
                                    fontSize: '14px',
                                    color: selected ? '#000' : '#666',
                                    backgroundColor: selected ? '#f0f0f0' : 'transparent',
                                    border: 'none',
                                    borderRadius: '4px',
                                  }}
                                  {...item}
                                >
                                  {itemPage}
                                </button>
                              );
                            } else {
                              children = (
                                <button
                                  type="button"
                                  style={{
                                    margin: '0 5px',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                    fontFamily: 'Poppins',
                                    fontSize: '14px',
                                    color: '#666',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                  }}
                                  {...item}
                                >
                                  {type === 'previous' ? 'Previous' : 'Next'}
                                </button>
                              );
                            }

                            return <li key={index}>{children}</li>;
                          })}
                        </List>
                      </nav>
                    </Box>
                  </>
                ) : (
                  <div className="empty-state-container">
                    {debouncedSearchTerm?.trim() ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#94a3b8',
                            textAlign: 'center',
                          }}
                        >
                          {`No banners found for "${debouncedSearchTerm.trim()}". Try a different search term.`}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 300,
                            height: 300,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Lottie
                            animationData={disputeAnimation}
                            loop={true}
                            autoplay={true}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#94a3b8',
                            textAlign: 'center',
                          }}
                        >
                          Start Creating Banner
                        </Typography>
                      </Box>
                    )}
                  </div>
                )}
              </div>

              {isModalOpen && (
                <CreateNewBannerModel onClose={handleCloseModal} onSuccess={handleBannerCreated} />
              )}

              <DeleteBanner
                isOpen={deleteModal.isOpen}
                onClose={handleCloseDeleteModal}
                onConfirm={handleDeleteConfirm}
                bannerTitle={deleteModal.bannerTitle}
              />
            </div>
          </Box>
        </Box>
      </Layout>
    </>
  );
};

export default Banner;
